import React from 'react';
import { Spin, Divider, Typography, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ExtractedLabelItem from '@/features/datasets/components/ExtractedLabelItem';
import LabelMappingColumnsHeader from '@/features/datasets/components/LabelMappingColumnsHeader';
import { getLabelColumnPluralLabel } from '@/shared/domain/taskTypes';
const { Text } = Typography;



/**
 * Generic mapping UI for extracted values (labels/entities/relations).
 *
 * Note: `availableLabels` is the universe of selectable replacements. If omitted,
 * we fall back to the extracted values + any newly created labels.
 * `taskLabels` is the task-defined catalog shown under "Labels in this task".
 */
const LabelExtractionManager = ({
    extractedLabels,
    loading,
    labelMappings,
    onDeleteLabel,
    onReplaceLabel,
    onAddNewLabel,
    labelType = 'Label', // "Label", "Entity", or "Relation"
    taskType,
    isMultilabel,
    labelExamples = {},
    availableLabels: availableLabelsProp = null,
    /** Task-defined catalog ("Labels in this task"). Shown as its own dropdown section. */
    taskLabels: taskLabelsProp = null,
    title,
    tooltip,
    loadingItemPluralLabel,
    /** Single-label classification fallback "Other" column (mutually exclusive). */
    showRejectionColumn = false,
    rejectionLabelName = null,
    onSetRejectionLabel,
}) => {
    const extractedList = Array.isArray(extractedLabels) ? extractedLabels : [];
    const mappings = labelMappings || {};
    const taskLabels = (Array.isArray(taskLabelsProp) ? taskLabelsProp : []).filter(Boolean);

    // Create a combined list of available labels that includes:
    // - task-defined catalog ("Labels in this task")
    // - provided options (existing labels in the project)
    // - extracted values
    // - any new labels from mappings
    const combinedAvailableLabels = [
        ...new Set([
            ...taskLabels,
            ...(Array.isArray(availableLabelsProp) ? availableLabelsProp : []),
            ...extractedList,
            ...Object.keys(mappings).filter((k) => !extractedList.includes(k)),
            ...Object.values(mappings).filter(Boolean),
        ]),
    ].filter(Boolean);

    const shouldEnableScroll = extractedList.length > 3;

    return (
        <div style={{ marginTop: '8px' }}>
            {title ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Text style={{ fontWeight: 600, color: '#222', fontSize: 14, margin: 0 }}>
                        {title}
                    </Text>
                    {tooltip ? (
                        <Tooltip title={tooltip}>
                            <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14, cursor: 'help' }} />
                        </Tooltip>
                    ) : null}
                </div>
            ) : null}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                    <div style={{ marginTop: '8px' }}>
                        Loading extracted {(loadingItemPluralLabel || getLabelColumnPluralLabel(taskType)).toLowerCase()}...
                    </div>
                </div>
            ) : (
                <>
                    {extractedList.length > 0 && (
                        <LabelMappingColumnsHeader
                            firstLabel={`${labelType} extracted`}
                            firstTooltip={`This ${labelType.toLowerCase()} was extracted from the dataset`}
                            secondLabel={`Replace with ${labelType}`}
                            secondTooltip={`Choose an existing ${labelType.toLowerCase()} to replace this one, or create a new ${labelType.toLowerCase()}`}
                            showRejectionColumn={showRejectionColumn}
                            paddingRight={shouldEnableScroll ? 6 : 0}
                        />
                    )}
                    <div
                        className={shouldEnableScroll ? 'optune-scrollbar' : ''}
                        style={{
                            maxHeight: shouldEnableScroll ? 'min(320px, 38vh)' : 'none',
                            overflowY: shouldEnableScroll ? 'auto' : 'visible',
                            paddingRight: shouldEnableScroll ? 6 : 0,
                        }}
                    >
                    {extractedList.map((label, index) => (
                        <div key={label}>
                            <ExtractedLabelItem
                                label={label}
                                onDelete={onDeleteLabel}
                                onReplace={onReplaceLabel}
                                availableLabels={combinedAvailableLabels}
                                taskLabels={taskLabels}
                                onAddNewLabel={onAddNewLabel}
                                labelType={labelType}
                                taskType={taskType}
                                isMultilabel={isMultilabel}
                                labelExample={labelExamples?.[label]}
                                labelMappings={mappings}
                                showRejectionColumn={showRejectionColumn}
                                rejectionLabelName={rejectionLabelName}
                                onSetRejectionLabel={onSetRejectionLabel}
                                showHeaders={false}
                            />
                            {index < extractedList.length - 1 && <Divider />}
                        </div>
                    ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default LabelExtractionManager;

